import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete,
  Search as SearchIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Star as FeaturedIcon,
} from '@mui/icons-material';
import JuntoApiService, { StoreItem, StoreItemVariant } from '../services/api';
import { logger } from '../utils/logger';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

// Store Item Categories
const STORE_CATEGORIES = [
  'apparel',
  'equipment',
  'accessories',
  'books',
  'supplements',
  'gifts',
  'other'
];

// Recurrence Types
const RECURRENCE_TYPES = [
  { value: 'none', label: 'One-time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StoreItemsPage: React.FC = () => {
  // State
  const [storeItems, setStoreItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StoreItem | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    is_featured: false,
    recurrence_type: 'none',
    max_purchases_per_user: null as number | null,
    is_promotional: false,
    promotional_text: '',
    display_order: 0,
    tags: [] as string[],
    image_url: '',
    category: 'apparel',
    subcategory: '',
    brand: '',
    sku: '',
    weight: null as number | null,
    dimensions: '',
    materials: '',
    care_instructions: '',
    has_variants: false,
    total_inventory: 0,
    low_stock_threshold: 5,
    requires_shipping: true,
    is_digital: false,
    shipping_weight: null as number | null,
    variants: [] as StoreItemVariant[],
  });
  
  // Variant editing
  const [newVariant, setNewVariant] = useState({
    size: '',
    color: '',
    sku: '',
    inventory_count: 0,
    price_adjustment: 0,
    is_available: true,
  });

  // Load data
  const loadStoreItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getStoreItems(includeInactive);
      setStoreItems(data);
      setError(null);
    } catch (err) {
      logger.error('Failed to load store items', err as Error);
      setError('Failed to load store items. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadStoreItems();
  }, [loadStoreItems]);


  // Filter items based on search
  const filteredItems = storeItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Dialog handlers
  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      currency: 'USD',
      is_featured: false,
      recurrence_type: 'none',
      max_purchases_per_user: null,
      is_promotional: false,
      promotional_text: '',
      display_order: 0,
      tags: [],
      image_url: '',
      category: 'apparel',
      subcategory: '',
      brand: '',
      sku: '',
      weight: null,
      dimensions: '',
      materials: '',
      care_instructions: '',
      has_variants: false,
      total_inventory: 0,
      low_stock_threshold: 5,
      requires_shipping: true,
      is_digital: false,
      shipping_weight: null,
      variants: [],
    });
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openEditDialog = (item: StoreItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price,
      currency: item.currency,
      is_featured: item.is_featured,
      recurrence_type: item.recurrence_type,
      max_purchases_per_user: item.max_purchases_per_user || null,
      is_promotional: item.is_promotional,
      promotional_text: item.promotional_text || '',
      display_order: item.display_order,
      tags: item.tags,
      image_url: item.image_url || '',
      category: item.category,
      subcategory: item.subcategory || '',
      brand: item.brand || '',
      sku: item.sku || '',
      weight: item.weight || null,
      dimensions: item.dimensions || '',
      materials: item.materials || '',
      care_instructions: item.care_instructions || '',
      has_variants: item.has_variants,
      total_inventory: item.total_inventory,
      low_stock_threshold: item.low_stock_threshold,
      requires_shipping: item.requires_shipping,
      is_digital: item.is_digital,
      shipping_weight: item.shipping_weight || null,
      variants: item.variants,
    });
    setDialogTab(0);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setDialogError(null);
    setDialogTab(0);
  };

  // Form handlers
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagsChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const tags = event.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    handleInputChange('tags', tags);
  };

  // Variant handlers
  const addVariant = () => {
    if (!newVariant.size && !newVariant.color) {
      setDialogError('Variant must have at least size or color specified');
      return;
    }

    const variant: StoreItemVariant = {
      id: `variant-${Date.now()}`,
      ...newVariant,
    };

    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, variant],
      has_variants: true,
    }));

    setNewVariant({
      size: '',
      color: '',
      sku: '',
      inventory_count: 0,
      price_adjustment: 0,
      is_available: true,
    });
  };

  const removeVariant = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
      has_variants: prev.variants.length > 1,
    }));
  };

  // CRUD operations
  const handleSave = async () => {
    try {
      setDialogError(null);

      if (!formData.name.trim()) {
        setDialogError('Item name is required');
        return;
      }

      if (formData.price < 0) {
        setDialogError('Price cannot be negative');
        return;
      }

      const itemData: any = {
        ...formData,
        purchasable_type: 'store_item',
      };

      if (editingItem) {
        await JuntoApiService.updateStoreItem({ 
          ...itemData, 
          id: editingItem.id,
          created_at: editingItem.created_at,
          updated_at: new Date().toISOString()
        });
      } else {
        await JuntoApiService.createStoreItem(itemData);
      }

      await loadStoreItems();
      closeDialog();
    } catch (err: any) {
      logger.error('Failed to save store item', err as Error);
      setDialogError(err.response?.data?.detail || 'Failed to save store item');
    }
  };


  // Computed properties
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totalValue = storeItems.reduce((sum, item) => sum + item.price, 0);
  const activeItems = storeItems.filter(item => item.is_active).length;
  const featuredItems = storeItems.filter(item => item.is_featured).length;
  const outOfStockItems = storeItems.filter(item => !item.is_digital && item.total_inventory === 0).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Store Items
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Store Item
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StoreIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Items
                  </Typography>
                  <Typography variant="h5">
                    {storeItems.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <FeaturedIcon color="secondary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Items
                  </Typography>
                  <Typography variant="h5">
                    {activeItems}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InventoryIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Featured Items
                  </Typography>
                  <Typography variant="h5">
                    {featuredItems}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ShippingIcon color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Out of Stock
                  </Typography>
                  <Typography variant="h5">
                    {outOfStockItems}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search items"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {STORE_CATEGORIES.map(category => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
              }
              label="Show Inactive"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                />
              }
              label="In Stock Only"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Inventory</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {item.name}
                        {item.is_featured && (
                          <Chip 
                            size="small" 
                            label="Featured" 
                            color="secondary" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      {item.description && (
                        <Typography variant="body2" color="textSecondary">
                          {item.description.length > 50 
                            ? `${item.description.substring(0, 50)}...` 
                            : item.description}
                        </Typography>
                      )}
                      {item.brand && (
                        <Typography variant="caption" color="textSecondary">
                          Brand: {item.brand}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                      size="small"
                    />
                    {item.subcategory && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {item.subcategory}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      ${item.price.toFixed(2)} {item.currency}
                    </Typography>
                    {item.is_promotional && (
                      <Chip 
                        size="small" 
                        label="Promo" 
                        color="primary" 
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.is_digital ? (
                      <Chip size="small" label="Digital" color="info" />
                    ) : (
                      <Box>
                        <Typography variant="body2">
                          {item.total_inventory} units
                        </Typography>
                        {item.has_variants && (
                          <Typography variant="caption" color="textSecondary">
                            {item.variants.length} variants
                          </Typography>
                        )}
                        {item.total_inventory <= item.low_stock_threshold && item.total_inventory > 0 && (
                          <Chip size="small" label="Low Stock" color="warning" />
                        )}
                        {item.total_inventory === 0 && (
                          <Chip size="small" label="Out of Stock" color="error" />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.is_active ? 'Active' : 'Inactive'}
                      color={item.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={item}
                      entityType="store-items"
                      entityDisplayName="Store Item"
                      onEdit={openEditDialog}
                      onRefresh={loadStoreItems}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Category:</strong> {entity.category}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Price:</strong> ${entity.price.toFixed(2)} {entity.currency}
                          </Typography>
                          <Typography variant="body1">
                            <strong>SKU:</strong> {entity.sku || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItem ? 'Edit Store Item' : 'Create Store Item'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={dialogTab} onChange={(_, newValue) => setDialogTab(newValue)}>
              <Tab label="Basic Info" />
              <Tab label="Product Details" />
              <Tab label="Inventory & Shipping" />
              <Tab label="Variants" />
            </Tabs>
          </Box>

          {/* Basic Info Tab */}
          <TabPanel value={dialogTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    label="Category"
                  >
                    {STORE_CATEGORIES.map(category => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  required
                  inputProps={{ step: 0.01, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Recurrence</InputLabel>
                  <Select
                    value={formData.recurrence_type}
                    onChange={(e) => handleInputChange('recurrence_type', e.target.value)}
                    label="Recurrence"
                  >
                    {RECURRENCE_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Purchases per User"
                  type="number"
                  value={formData.max_purchases_per_user || ''}
                  onChange={(e) => handleInputChange('max_purchases_per_user', e.target.value ? parseInt(e.target.value) : null)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  defaultValue={formData.tags.join(', ')}
                  onBlur={handleTagsChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_featured}
                      onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                    />
                  }
                  label="Featured"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_promotional}
                      onChange={(e) => handleInputChange('is_promotional', e.target.checked)}
                    />
                  }
                  label="Promotional"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Product Details Tab */}
          <TabPanel value={dialogTab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Brand"
                  value={formData.brand}
                  onChange={(e) => handleInputChange('brand', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subcategory"
                  value={formData.subcategory}
                  onChange={(e) => handleInputChange('subcategory', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (grams)"
                  type="number"
                  value={formData.weight || ''}
                  onChange={(e) => handleInputChange('weight', e.target.value ? parseFloat(e.target.value) : null)}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dimensions"
                  value={formData.dimensions}
                  onChange={(e) => handleInputChange('dimensions', e.target.value)}
                  placeholder="e.g., 10cm x 5cm x 2cm"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Materials"
                  value={formData.materials}
                  onChange={(e) => handleInputChange('materials', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Care Instructions"
                  value={formData.care_instructions}
                  onChange={(e) => handleInputChange('care_instructions', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image_url}
                  onChange={(e) => handleInputChange('image_url', e.target.value)}
                />
              </Grid>
              {formData.is_promotional && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Promotional Text"
                    value={formData.promotional_text}
                    onChange={(e) => handleInputChange('promotional_text', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Inventory & Shipping Tab */}
          <TabPanel value={dialogTab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Inventory"
                  type="number"
                  value={formData.total_inventory}
                  onChange={(e) => handleInputChange('total_inventory', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Low Stock Threshold"
                  type="number"
                  value={formData.low_stock_threshold}
                  onChange={(e) => handleInputChange('low_stock_threshold', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Shipping Weight (grams)"
                  type="number"
                  value={formData.shipping_weight || ''}
                  onChange={(e) => handleInputChange('shipping_weight', e.target.value ? parseFloat(e.target.value) : null)}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Display Order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => handleInputChange('display_order', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requires_shipping}
                      onChange={(e) => handleInputChange('requires_shipping', e.target.checked)}
                    />
                  }
                  label="Requires Shipping"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_digital}
                      onChange={(e) => handleInputChange('is_digital', e.target.checked)}
                    />
                  }
                  label="Digital Product"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.has_variants}
                      onChange={(e) => handleInputChange('has_variants', e.target.checked)}
                    />
                  }
                  label="Has Variants"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Variants Tab */}
          <TabPanel value={dialogTab} index={3}>
            <Typography variant="h6" gutterBottom>
              Product Variants
            </Typography>
            
            {/* Add Variant Form */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Add New Variant
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Size"
                    value={newVariant.size}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, size: e.target.value }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Color"
                    value={newVariant.color}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, color: e.target.value }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Inventory"
                    type="number"
                    value={newVariant.inventory_count}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, inventory_count: parseInt(e.target.value) || 0 }))}
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Price +/-"
                    type="number"
                    value={newVariant.price_adjustment}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, price_adjustment: parseFloat(e.target.value) || 0 }))}
                    size="small"
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    onClick={addVariant}
                    size="small"
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Existing Variants */}
            {formData.variants.length > 0 ? (
              <List>
                {formData.variants.map((variant, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="subtitle2">
                              {[variant.size, variant.color].filter(Boolean).join(' - ') || 'Variant'}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={`${variant.inventory_count} units`}
                              color={variant.inventory_count > 0 ? 'success' : 'error'}
                            />
                            {variant.price_adjustment !== 0 && (
                              <Chip 
                                size="small" 
                                label={`${variant.price_adjustment >= 0 ? '+' : ''}$${variant.price_adjustment.toFixed(2)}`}
                                color="primary"
                              />
                            )}
                          </Box>
                        }
                        secondary={variant.sku ? `SKU: ${variant.sku}` : undefined}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeVariant(index)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < formData.variants.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary" textAlign="center" py={2}>
                No variants added yet
              </Typography>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreItemsPage;